import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import FeeConcessionModal from './FeeConcessionModal';
import BulkFeeConcessionModal from './BulkFeeConcessionModal';
import './FeeConcessions.css';

const CURRENT_EDITABLE_YEAR = '2025-2026';
const ACADEMIC_YEARS = ['2024-2025', '2025-2026', '2026-2027'];

export default function FeeConcessions({ orgId: orgIdProp, isTab = false }) {
  const navigate = useNavigate();
  const { axios: authAxios, token, user } = useAuth();
  const orgId = orgIdProp || user?.orgId || '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(CURRENT_EDITABLE_YEAR);
  const [searchQuery, setSearchQuery] = useState('');
  const [concessionFilter, setConcessionFilter] = useState('ALL'); // ALL, CONCESSION_ONLY, STANDARD_ONLY

  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classOptions, setClassOptions] = useState([]);

  const [loadingData, setLoadingData] = useState(false);
  const [students, setStudents] = useState([]);
  const [baseFeeStructure, setBaseFeeStructure] = useState(null);
  const [studentFeeProfiles, setStudentFeeProfiles] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  // Fetch Class Options
  useEffect(() => {
    const fetchClassOptions = async () => {
      if (!orgId) return;
      try {
        setLoadingClasses(true);
        const { data } = await authAxios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
        const options = data
          .map(cls => {
            const section = cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined' ? String(cls.sectionName).trim() : '';
            return {
              value: cls.classId,
              label: section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim()
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setClassOptions(options);
        if (options.length > 0 && !selectedClass) {
          setSelectedClass(options[0].value);
        }
      } catch (err) {
        console.error('Failed to fetch class options:', err);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClassOptions();
  }, [orgId, authAxios, selectedClass]);

  // Load Students, Fee Structure, & Student Fee Profiles for Selected Class
  const loadClassData = useCallback(async () => {
    if (!selectedClass || !selectedAcademicYear || !orgId) return;

    try {
      setLoadingData(true);

      // 1. Fetch Students in Class
      let classStudents = [];
      try {
        const studentRes = await authAxios.get(`${configData.GET_STUDENTS_BY_CLASS}/${selectedClass}`, {
          params: { page: 0, pageSize: 200 }
        });
        const items = studentRes.data?.items || studentRes.data?.content || (Array.isArray(studentRes.data) ? studentRes.data : []);
        classStudents = items.map(item => {
          const rawUser = item.user || item.rawData?.user || item;
          const rawStudent = item.studentInfo || item.rawData?.studentInfo || {};
          return {
            ...item,
            userId: rawUser.userId || item.userId || item.id,
            displayName: rawUser.displayName || `${rawUser.firstName || ''} ${rawUser.lastName || ''}`.trim() || item.displayName || 'Student',
            firstName: rawUser.firstName || item.firstName,
            lastName: rawUser.lastName || item.lastName,
            className: item.classDetails?.className || rawStudent.className || item.className,
            classId: selectedClass
          };
        });
      } catch (sErr) {
        console.warn('GET_STUDENTS_BY_CLASS failed, attempting fallback:', sErr);
        try {
          const fallbackRes = await authAxios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
            params: { userType: 'STUDENT', page: 1, pageSize: 500 }
          });
          const allStudents = Array.isArray(fallbackRes.data) ? fallbackRes.data : (fallbackRes.data?.items || fallbackRes.data?.content || []);
          classStudents = allStudents.filter(s => {
            const sClass = String(s.classId || s.class_id || s.assignedClass || s.studentInfo?.classId || '');
            return sClass === String(selectedClass);
          });
        } catch (fErr) {
          console.error('Fallback student fetch failed:', fErr);
        }
      }

      // 2. Fetch Base FeeStructure for Class & Academic Year
      let feeStruct = null;
      try {
        const feeRes = await authAxios.get(
          `${configData.GET_FEE_STRUCTURE}/class/${selectedClass}?academicYear=${selectedAcademicYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const raw = Array.isArray(feeRes.data) ? feeRes.data : (feeRes.data?._embedded?.feeStructures || feeRes.data?.items || (feeRes.data ? [feeRes.data] : []));
        feeStruct = raw.find(i => i && i.academicYear === selectedAcademicYear) || raw[0] || null;
      } catch (fErr) {
        console.warn('No base fee structure found for class:', fErr);
      }

      // 3. Fetch Student Fee Profiles (Overrides) for Class
      const profileMap = {};
      try {
        const profileRes = await authAxios.get(
          `${configData.STUDENT_FEE_PROFILE}/class/${selectedClass}?orgId=${orgId}&academicYear=${selectedAcademicYear}&size=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const profilesList = profileRes.data?.items || (Array.isArray(profileRes.data) ? profileRes.data : []);
        profilesList.forEach(p => {
          if (p.studentId) {
            profileMap[p.studentId] = p;
          }
        });
      } catch (pErr) {
        console.warn('No student fee profiles found for class:', pErr);
      }

      setStudents(classStudents);
      setBaseFeeStructure(feeStruct);
      setStudentFeeProfiles(profileMap);
    } catch (err) {
      console.error('Error loading concession data:', err);
      toast.error('Failed to load fee concession data.');
    } finally {
      setLoadingData(false);
    }
  }, [selectedClass, selectedAcademicYear, orgId, authAxios, token]);

  useEffect(() => {
    loadClassData();
  }, [loadClassData]);

  // Compute Base Annual Total Fee
  const baseAnnualFee = useMemo(() => {
    if (!baseFeeStructure) return 0;
    const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
    return installments.reduce((acc, inst) => {
      const val = inst.feeBreakdown
        ? Object.values(inst.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
        : Number(inst.totalFee || inst.amount || 0);
      return acc + val;
    }, 0);
  }, [baseFeeStructure]);

  // Filtered Students List
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const sId = String(student.userId || student.id || '');
      const sName = `${student.firstName || ''} ${student.lastName || ''} ${student.displayName || ''}`.toLowerCase();
      const matchesSearch = !searchQuery || sName.includes(searchQuery.toLowerCase()) || sId.toLowerCase().includes(searchQuery.toLowerCase());

      const profile = studentFeeProfiles[sId];
      const hasConcession = profile && profile.studentFeeOverrides && profile.studentFeeOverrides.some(ov => ov.discounts && ov.discounts.length > 0);

      if (concessionFilter === 'CONCESSION_ONLY') return matchesSearch && hasConcession;
      if (concessionFilter === 'STANDARD_ONLY') return matchesSearch && !hasConcession;
      return matchesSearch;
    });
  }, [students, searchQuery, concessionFilter, studentFeeProfiles]);

  // Stats Summary
  const stats = useMemo(() => {
    const totalStudents = students.length;
    let concessionCount = 0;
    let totalDiscountAmount = 0;

    students.forEach(s => {
      const sId = String(s.userId || s.id || '');
      const profile = studentFeeProfiles[sId];
      if (profile && profile.studentFeeOverrides) {
        let studentDisc = 0;
        profile.studentFeeOverrides.forEach(ov => {
          if (ov.discounts) {
            ov.discounts.forEach(d => {
              studentDisc += Number(d.amount || 0);
            });
          }
        });
        if (studentDisc > 0) {
          concessionCount++;
          totalDiscountAmount += studentDisc;
        }
      }
    });

    return {
      totalStudents,
      concessionCount,
      totalDiscountAmount
    };
  }, [students, studentFeeProfiles]);

  const handleOpenSingleModal = (student) => {
    if (!baseFeeStructure) {
      toast.error('No base Fee Structure found for this class. Please create a Fee Plan first.');
      return;
    }
    setSelectedStudentForModal(student);
    setModalOpen(true);
  };

  const handleOpenBulkModal = () => {
    if (!baseFeeStructure) {
      toast.error('No base Fee Structure found for this class. Please create a Fee Plan first.');
      return;
    }
    setBulkModalOpen(true);
  };

  const handleRemoveProfile = async (profileId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove all fee concessions for ${studentName}?`)) return;

    try {
      await authAxios.delete(`${configData.STUDENT_FEE_PROFILE}/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Removed fee concessions for ${studentName}.`);
      loadClassData();
    } catch (err) {
      console.error('Failed to delete student fee profile:', err);
      toast.error('Failed to remove concession.');
    }
  };

  return (
    <div className="concession-container" style={{ padding: isTab ? '16px 24px' : '24px' }}>
      {/* Top Header (only when not rendered as a tab) */}
      {!isTab && (
        <div className="concession-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconButton onClick={() => navigate('/finance')} size="small" style={{ color: '#014486' }}>
                <ArrowBackIcon />
              </IconButton>
              <h1 className="concession-title">Student Fee Concessions</h1>
            </div>
          </div>

          <Button
            variant="outlined"
            onClick={() => navigate('/finance')}
            style={{ borderColor: '#cbd5e1', color: '#475569', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            View Class Fee Plans
          </Button>
        </div>
      )}

      {/* Toolbar / Filters */}
      <div className="concession-toolbar">
        <div className="concession-filters-left">
          <div className="concession-filter-group">
            <label>Grade / Class</label>
            {loadingClasses ? (
              <CircularProgress size={20} />
            ) : (
              <Select
                className="concession-select"
                size="small"
                value={selectedClass}
                onChange={e => setSelectedClass(e.target.value)}
              >
                {classOptions.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>

          <div className="concession-filter-group">
            <label>Academic Year</label>
            <Select
              className="concession-select"
              size="small"
              value={selectedAcademicYear}
              onChange={e => setSelectedAcademicYear(e.target.value)}
            >
              {ACADEMIC_YEARS.map(y => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="concession-filter-group">
            <label>Concession Status</label>
            <Select
              className="concession-select"
              size="small"
              value={concessionFilter}
              onChange={e => setConcessionFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Students</MenuItem>
              <MenuItem value="CONCESSION_ONLY">With Concessions Only</MenuItem>
              <MenuItem value="STANDARD_ONLY">Standard Fee Only</MenuItem>
            </Select>
          </div>

          <div className="concession-filter-group" style={{ flex: 1 }}>
            <label>Search Student</label>
            <div className="concession-search-box">
              <SearchIcon className="concession-search-icon" />
              <input
                className="concession-search-input"
                placeholder="Search student name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Summary Bar & Common Apply Concession Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div className="concession-stat-card" style={{ minWidth: '220px' }}>
            <div className="concession-stat-icon">
              <PeopleIcon />
            </div>
            <div className="concession-stat-info">
              <span className="concession-stat-value">{stats.totalStudents}</span>
              <span className="concession-stat-label">Students in Class</span>
            </div>
          </div>

          <div className="concession-stat-card" style={{ minWidth: '220px' }}>
            <div className="concession-stat-icon" style={{ background: '#ecfdf5', color: '#047857' }}>
              <LocalOfferIcon />
            </div>
            <div className="concession-stat-info">
              <span className="concession-stat-value" style={{ color: '#047857' }}>{stats.concessionCount}</span>
              <span className="concession-stat-label">Concessions Granted</span>
            </div>
          </div>
        </div>

        <Button
          variant="contained"
          onClick={handleOpenBulkModal}
          startIcon={<AddIcon />}
          style={{
            backgroundColor: '#014486',
            color: '#ffffff',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            padding: '12px 24px',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(1, 68, 134, 0.28)'
          }}
        >
          Apply Concession
        </Button>
      </div>

      {/* Table Section */}
      <div className="concession-table-card">
        {loadingData ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <CircularProgress style={{ color: '#014486' }} />
          </div>
        ) : filteredStudents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <SchoolIcon style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
            <h3 style={{ margin: 0, color: '#334155' }}>No students found</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '14px' }}>
              {!selectedClass ? 'Please select a Grade / Class above' : 'No student profiles matching your search or filters.'}
            </p>
          </div>
        ) : (
          <table className="concession-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Base Class Fee</th>
                <th>Applied Concessions</th>
                <th>Net Annual Fee</th>
                <th>Remarks / Reason</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(student => {
                const sId = String(student.userId || student.id || '');
                const sName = student.displayName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student';
                const profile = studentFeeProfiles[sId];

                let totalDiscount = 0;
                const discountsList = [];
                if (profile && profile.studentFeeOverrides) {
                  profile.studentFeeOverrides.forEach(ov => {
                    if (ov.discounts) {
                      ov.discounts.forEach(d => {
                        totalDiscount += Number(d.amount || 0);
                        discountsList.push(d);
                      });
                    }
                  });
                }

                const netFee = profile?.netTotalFee !== undefined && profile?.netTotalFee !== null
                  ? profile.netTotalFee
                  : Math.max(0, baseAnnualFee - totalDiscount);

                const hasConcession = totalDiscount > 0;

                return (
                  <tr key={sId}>
                    <td style={{ fontWeight: 600, color: '#0f172a' }}>
                      {sName}
                    </td>

                    <td style={{ fontWeight: 600, color: '#475569' }}>
                      ₹{baseAnnualFee.toLocaleString('en-IN')}
                    </td>

                    <td>
                      {hasConcession ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className="badge-concession">
                            <LocalOfferIcon style={{ fontSize: '14px' }} />
                            - ₹{totalDiscount.toLocaleString('en-IN')} Discount
                          </span>
                          {discountsList.slice(0, 2).map((d, i) => (
                            <span key={i} style={{ fontSize: '11px', color: '#047857' }}>
                              • {d.title} ({d.discountType === 'PERCENTAGE' ? `${d.value}%` : `₹${d.amount}`})
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="badge-standard">Standard Fee (No Discount)</span>
                      )}
                    </td>

                    <td style={{ fontWeight: 800, fontSize: '15px', color: hasConcession ? '#014486' : '#0f172a' }}>
                      ₹{netFee.toLocaleString('en-IN')}
                    </td>

                    <td>
                      {discountsList.length > 0 && discountsList[0].remarks ? (
                        <span className="remarks-text" title={discountsList[0].remarks}>
                          "{discountsList[0].remarks}"
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button
                          size="small"
                          variant={hasConcession ? "outlined" : "contained"}
                          onClick={() => handleOpenSingleModal(student)}
                          startIcon={hasConcession ? <EditIcon /> : <AddIcon />}
                          style={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            backgroundColor: hasConcession ? 'transparent' : '#014486',
                            color: hasConcession ? '#014486' : '#ffffff',
                            borderColor: '#014486'
                          }}
                        >
                          {hasConcession ? 'Edit Concession' : 'Apply Concession'}
                        </Button>

                        {profile && (
                          <Tooltip title="Remove Concession (Reset to Standard Fee)">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveProfile(profile.studentFeeProfileId, sName)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Single Fee Concession Modal */}
      {modalOpen && (
        <FeeConcessionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          selectedStudent={selectedStudentForModal}
          baseFeeStructure={baseFeeStructure}
          existingProfile={selectedStudentForModal ? studentFeeProfiles[String(selectedStudentForModal.userId || selectedStudentForModal.id || '')] : null}
          selectedClassId={selectedClass}
          academicYear={selectedAcademicYear}
          onSaveSuccess={() => {
            loadClassData();
          }}
        />
      )}

      {/* Multi / Bulk Fee Concession Modal */}
      {bulkModalOpen && (
        <BulkFeeConcessionModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          students={students}
          baseFeeStructure={baseFeeStructure}
          studentFeeProfiles={studentFeeProfiles}
          selectedClassId={selectedClass}
          academicYear={selectedAcademicYear}
          onSaveSuccess={() => {
            loadClassData();
          }}
        />
      )}
    </div>
  );
}


